import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { Session } from "../lib/auth";
import {
  getMannequinMetadata,
  getMannequinSurface,
  type MannequinFeature,
  type MannequinMetadata,
  type MannequinSurfaceRequest,
  type MannequinSurfaceResponse,
} from "../api/mannequin";
import MannequinSurfaceGrid, { type MannequinViewMode } from "./MannequinSurfaceGrid";
import "./Mannequin.css";
// MathJax v3 tex-svg runtime, bundled from the `mathjax` npm dependency and
// emitted by Vite as a local, hashed asset. SVG output avoids CHTML web font
// lookups when the bundle is no longer served from MathJax's es5/ layout.
import mathJaxSrc from "mathjax/es5/tex-svg.js?url";

type Props = {
  session: Session | null;
};

type DocId = "intro" | "aerodas-prior" | "vehicle-coupling" | "pathologies" | "tailor-training";

const DOC_ORDER: DocId[] = [
  "intro",
  "aerodas-prior",
  "vehicle-coupling",
  "pathologies",
  "tailor-training",
];

const DOCS: Record<DocId, { title: string; body: Array<{ kind: "p" | "math" | "list"; text?: string; items?: string[] }> }> = {
  intro: {
    title: "Mannequin Overview",
    body: [
      {
        kind: "p",
        text: "Mannequin is a synthetic aerodynamic database generator for Thread and Tailor demos. It produces deterministic aircraft response surfaces with enough nonlinear structure to exercise adaptive sampling, surrogate fitting, and model-audit workflows without running CFD.",
      },
      {
        kind: "math",
        text: String.raw`\mathbf{x} = [\alpha,\beta,\delta_a,\delta_e,\delta_r,P,Q,R,\dot{\alpha},M,b,Re,\tau]`,
      },
      {
        kind: "math",
        text: String.raw`\mathbf{y}(\mathbf{x}) = [C_L,C_D,C_Y,C_l,C_m,C_n,C_{L,\mathrm{hyst}}]`,
      },
      {
        kind: "p",
        text: "The simulator combines an AERODAS-shaped longitudinal prior, multi-axis coupling, and optional sampled pathologies. The bright overlays on the surface are ground-truth feature locations, so reviewers can see where Tailor should spend samples.",
      },
    ],
  },
  "aerodas-prior": {
    title: "AERODAS Prior",
    body: [
      {
        kind: "p",
        text: "The low-dimensional prior is built from Spera/Erturk AERODAS-style lift and drag behavior. Mannequin keeps a literal Spera mode and a smoother differentiable mode for training and inspection.",
      },
      {
        kind: "math",
        text: String.raw`C_L(\alpha) \approx f_{\mathrm{AERODAS}}(\alpha; A_0, C_{L,\max}, S_1, S_2)`,
      },
      {
        kind: "math",
        text: String.raw`C_D(\alpha) \approx C_{D,0} + k C_L(\alpha)^2 + \Delta C_D(\delta, p, q, r)`,
      },
      {
        kind: "p",
        text: "Real public-domain airfoils project cleanly onto the same synthetic basis, which makes the surfaces plausible while still being controllable and deterministic.",
      },
    ],
  },
  "vehicle-coupling": {
    title: "Vehicle Coupling",
    body: [
      {
        kind: "p",
        text: "Mannequin does not simply add independent linear derivatives. The visible surface is a slice through a coupled high-dimensional aircraft model.",
      },
      {
        kind: "math",
        text: String.raw`\alpha_{\mathrm{eff}}=\alpha-\lambda_{\beta}\beta^2+\lambda_{\dot{\alpha}}\dot{\alpha}+\lambda_q \hat{q}`,
      },
      {
        kind: "math",
        text: String.raw`C_L = C_{L,\mathrm{prior}}(\alpha_{\mathrm{eff}}) + C_{L,\delta_e}\delta_e + C_{L,q}\hat{q} - \lambda_{\beta^2}\beta^2`,
      },
      {
        kind: "list",
        items: [
          "Conventional aircraft expose stall movement, control reversal, and directional-stability loss.",
          "Powered-lift aircraft use blowing and throttle to delay stall and change control authority.",
          "Schmidt reference families expose condition-dependent nonlinear derivative behavior.",
        ],
      },
    ],
  },
  pathologies: {
    title: "Sampled Pathologies",
    body: [
      {
        kind: "p",
        text: "The pathology switch samples deterministic ground-truth defects from the configured distribution. Each defect returns a manifold that the frontend draws when it intersects the current slice.",
      },
      {
        kind: "math",
        text: String.raw`\alpha_{\mathrm{stall}}=\alpha_0-k_{\beta}\beta^2-k_M M-k_{\delta_e}\delta_e`,
      },
      {
        kind: "math",
        text: String.raw`M_{\mathrm{crit}}=M_{\mathrm{crit},0}-k_{\alpha}\left(1-\cos\alpha\right)`,
      },
      {
        kind: "math",
        text: String.raw`|\delta_e|=\delta_{e,\max,0}-k_{\alpha}|\alpha|-k_{\beta}|\beta|`,
      },
      {
        kind: "math",
        text: String.raw`\Delta C = A\exp\left[-\left(\frac{\alpha-\alpha_c}{w_{\alpha}}\right)^2-\left(\frac{\beta-\beta_c}{w_{\beta}}\right)^2\right]`,
      },
    ],
  },
  "tailor-training": {
    title: "Tailor Training",
    body: [
      {
        kind: "p",
        text: "Tailor uses these surfaces as a controlled world where the right sampling behavior is auditable. The frontend is deliberately honest: it shows both the response surface and the hidden feature geometry Tailor is meant to discover from samples.",
      },
      {
        kind: "math",
        text: String.raw`\mathcal{L}_{\mathrm{Tailor}} = w_v \sigma^2(\mathbf{x}) + w_g \|\nabla \hat{f}(\mathbf{x})\| + w_t \tau(\mathbf{x}) + w_c d_{\mathrm{coverage}}(\mathbf{x})`,
      },
      {
        kind: "p",
        text: "The Mannequin route is Flexcompute-only because it exposes internal sampler behavior, synthetic-data assumptions, and ground-truth pathology labels used in product development.",
      },
    ],
  },
};

declare global {
  interface Window {
    MathJax?: {
      startup?: {
        typeset?: boolean;
      };
      svg?: {
        fontCache?: "local" | "global" | "none";
      };
      typesetPromise?: (elements?: Element[]) => Promise<void>;
    };
  }
}

function isFlexcompute(session: Session | null): boolean {
  return Boolean(session?.user?.email?.toLowerCase().endsWith("@flexcompute.com"));
}

// Local-dev parity with App.tsx's isLocalAuthBypassOrigin(): the local stack
// mints no Keycloak sessions (session stays null), so the flexcompute email
// gate would permanently blank this page on localhost even though the rest of
// the app treats the same origin as authenticated. Same guard as App.tsx —
// dev/local builds on loopback-style hostnames only; deployed builds are
// unaffected and keep the SSO gate.
function isLocalDevBypass(): boolean {
  if (!import.meta.env.DEV && import.meta.env.VITE_THREAD_ENV !== "local") {
    return false;
  }
  if (typeof window === "undefined") return false;
  return [
    "localhost",
    "127.0.0.1",
    "::1",
    "thread-local.simulation.cloud",
  ].includes(window.location.hostname);
}

function routeDoc(pathname: string): DocId | null {
  const match = pathname.match(/\/mannequin\/docs\/?([^/]*)/);
  if (!match) return null;
  const raw = match[1] || "intro";
  return DOC_ORDER.includes(raw as DocId) ? (raw as DocId) : "intro";
}

function MathText({ children, block = false }: { children: string; block?: boolean }) {
  useEffect(() => {
    const typesetMath = () => {
      void window.MathJax?.typesetPromise?.();
    };

    if (window.MathJax?.typesetPromise) {
      typesetMath();
      return;
    }

    if (!window.MathJax) {
      window.MathJax = {
        startup: { typeset: false },
        svg: { fontCache: "global" },
      };
    }

    const existingScript = document.querySelector<HTMLScriptElement>("script[data-thread-mathjax]");
    if (existingScript) {
      existingScript.addEventListener("load", typesetMath, { once: true });
      return () => existingScript.removeEventListener("load", typesetMath);
    }

    const script = document.createElement("script");
    script.src = mathJaxSrc;
    script.async = true;
    script.dataset.threadMathjax = "true";
    script.addEventListener("load", typesetMath, { once: true });
    document.head.appendChild(script);
    return () => script.removeEventListener("load", typesetMath);
  }, [children]);

  return (
    <span className={block ? "mannequin-equation block" : "mannequin-equation"}>
      {block ? String.raw`\[${children}\]` : String.raw`\(${children}\)`}
    </span>
  );
}

function formatNumber(value: number | undefined): string {
  if (value === undefined || Number.isNaN(value)) return "n/a";
  return Math.abs(value) >= 100 ? value.toFixed(1) : value.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}

function defaultRequest(metadata: MannequinMetadata): MannequinSurfaceRequest {
  const held = Object.fromEntries(
    metadata.inputs.map((name) => [name, metadata.input_config[name]?.default ?? 0]),
  );
  return {
    vehicle: "fighter_like",
    seed: 7,
    method: "smooth",
    pathologies: "sampled",
    x_axis: "alpha",
    y_axis: "beta",
    output: "CL",
    n: 45,
    held,
  };
}

function featureTitle(feature: MannequinFeature): string {
  return feature.type.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");
}

function visibleStats(surface: MannequinSurfaceResponse | null, outputs: string[]) {
  const values = outputs.flatMap((output) => {
    const z = surface?.output_surfaces[output]?.z ?? [];
    return z.flat().filter(Number.isFinite);
  });
  if (!values.length) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
  return { min, mean, max };
}

export default function MannequinPage({ session }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const activeDoc = routeDoc(location.pathname);
  const [metadata, setMetadata] = useState<MannequinMetadata | null>(null);
  const [request, setRequest] = useState<MannequinSurfaceRequest | null>(null);
  const [surface, setSurface] = useState<MannequinSurfaceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [surfaceLoading, setSurfaceLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<MannequinViewMode>("all");

  const flexcomputeUser = isFlexcompute(session) || isLocalDevBypass();

  useEffect(() => {
    if (!flexcomputeUser) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getMannequinMetadata()
      .then((data) => {
        if (cancelled) return;
        setMetadata(data);
        setRequest(defaultRequest(data));
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [flexcomputeUser]);

  useEffect(() => {
    if (!request || activeDoc) return;
    let cancelled = false;
    const handle = window.setTimeout(() => {
      setSurfaceLoading(true);
      getMannequinSurface(request)
        .then((data) => {
          if (!cancelled) {
            setSurface(data);
            setError(null);
          }
        })
        .catch((err) => {
          if (!cancelled) setError(err instanceof Error ? err.message : String(err));
        })
        .finally(() => {
          if (!cancelled) setSurfaceLoading(false);
        });
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [request, activeDoc]);

  function updateRequest(patch: Partial<MannequinSurfaceRequest>) {
    setRequest((current) => {
      if (!current) return current;
      const next = { ...current, ...patch };
      if (next.x_axis === next.y_axis) {
        const replacement = metadata?.inputs.find((axis) => axis !== next.x_axis) ?? "beta";
        next.y_axis = replacement;
      }
      return next;
    });
  }

  function updateHeld(axis: string, value: number) {
    setRequest((current) => current ? {
      ...current,
      held: { ...current.held, [axis]: value },
    } : current);
  }

  if (!flexcomputeUser) {
    return (
      <main className="mannequin-page denied">
        <section className="mannequin-denied-panel">
          <h1>Mannequin</h1>
          <p>This simulator is restricted to Flexcompute email addresses.</p>
          <p className="muted">Sign in with Google SSO using an <code>@flexcompute.com</code> account to continue.</p>
        </section>
      </main>
    );
  }

  if (loading || !metadata || !request) {
    return (
      <main className="mannequin-page denied">
        <section className="mannequin-denied-panel">
          <p>Loading Mannequin...</p>
        </section>
      </main>
    );
  }

  if (activeDoc) {
    const doc = DOCS[activeDoc];
    return (
      <main className="mannequin-page docs">
        <nav className="mannequin-topbar">
          <Link to="/mannequin" className="brand">Mannequin</Link>
          <div className="topbar-links">
            <Link to="/mannequin">Simulator</Link>
            <Link to="/mannequin/docs/intro" className="active">Docs</Link>
          </div>
        </nav>
        <div className="mannequin-doc-shell">
          <aside className="mannequin-doc-nav">
            {DOC_ORDER.map((docId) => (
              <button
                key={docId}
                className={docId === activeDoc ? "active" : ""}
                onClick={() => navigate(`/mannequin/docs/${docId}`)}
              >
                {DOCS[docId].title}
              </button>
            ))}
          </aside>
          <article className="mannequin-doc">
            <h1>{doc.title}</h1>
            {doc.body.map((block, index) => {
              if (block.kind === "math" && block.text) {
                return <MathText key={index} block>{block.text}</MathText>;
              }
              if (block.kind === "list" && block.items) {
                return (
                  <ul key={index}>
                    {block.items.map((item) => <li key={item}>{item}</li>)}
                  </ul>
                );
              }
              return <p key={index}>{block.text}</p>;
            })}
          </article>
        </div>
      </main>
    );
  }

  const activeAxes = new Set([request.x_axis, request.y_axis]);
  const visibleOutputs = surface ? (viewMode === "all" ? surface.outputs : [request.output]) : [request.output];
  const aggregateStats = visibleStats(surface, visibleOutputs);
  const visibleOverlayCount = visibleOutputs.reduce(
    (count, output) => count + (surface?.output_surfaces[output]?.overlays.length ?? 0),
    0,
  );

  return (
    <main className="mannequin-page">
      <nav className="mannequin-topbar">
        <Link to="/mannequin" className="brand">Mannequin</Link>
        <div className="topbar-links">
          <Link to="/mannequin" className="active">Simulator</Link>
          <Link to="/mannequin/docs/intro">Docs</Link>
        </div>
      </nav>

      <section className="mannequin-workbench">
        <aside className="mannequin-sidebar">
          <div className="control-section">
            <div className="section-title">Model</div>
            <label>
              Aircraft
              <select value={request.vehicle} onChange={(event) => updateRequest({ vehicle: event.target.value })}>
                {metadata.vehicles.map((vehicle) => (
                  <option key={vehicle} value={vehicle}>
                    {metadata.vehicle_labels[vehicle] ?? vehicle}
                  </option>
                ))}
              </select>
            </label>
            <div className="inline-controls">
              <label>
                Seed
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={request.seed}
                  onChange={(event) => updateRequest({ seed: Number(event.target.value) })}
                />
              </label>
              <label>
                AERODAS
                <select value={request.method} onChange={(event) => updateRequest({ method: event.target.value as "spera" | "smooth" })}>
                  <option value="spera">spera</option>
                  <option value="smooth">smooth</option>
                </select>
              </label>
            </div>
            <label>
              Pathologies
              <select value={request.pathologies} onChange={(event) => updateRequest({ pathologies: event.target.value as "none" | "sampled" })}>
                <option value="none">none</option>
                <option value="sampled">sampled</option>
              </select>
            </label>
          </div>

          <div className="control-section">
            <div className="section-title">Visible Slice</div>
            <div className="inline-controls">
              <label>
                X
                <select value={request.x_axis} onChange={(event) => updateRequest({ x_axis: event.target.value })}>
                  {metadata.inputs.map((axis) => <option key={axis}>{axis}</option>)}
                </select>
              </label>
              <label>
                Y
                <select value={request.y_axis} onChange={(event) => updateRequest({ y_axis: event.target.value })}>
                  {metadata.inputs.map((axis) => <option key={axis}>{axis}</option>)}
                </select>
              </label>
            </div>
            <div className="inline-controls">
              <label>
                Output
                <select value={request.output} onChange={(event) => updateRequest({ output: event.target.value })}>
                  {metadata.outputs.map((output) => (
                    <option key={output} value={output}>
                      {output} - {metadata.output_labels[output] ?? output}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                View
                <select value={viewMode} onChange={(event) => setViewMode(event.target.value as MannequinViewMode)}>
                  <option value="all">all outputs</option>
                  <option value="single">focused</option>
                </select>
              </label>
            </div>
            <div className="inline-controls">
              <label>
                Grid
                <input
                  type="number"
                  min={20}
                  max={80}
                  step={5}
                  value={request.n}
                  onChange={(event) => updateRequest({ n: Number(event.target.value) })}
                />
              </label>
            </div>
          </div>

          <div className="control-section held">
            <div className="section-title">Held Values</div>
            {metadata.inputs.filter((axis) => !activeAxes.has(axis)).map((axis) => {
              const cfg = metadata.input_config[axis];
              if (!cfg) return null;
              return (
                <label key={axis} className="range-row">
                  <span>{axis} <em>{cfg.unit}</em></span>
                  <input
                    type="range"
                    min={cfg.min}
                    max={cfg.max}
                    step={cfg.step}
                    value={request.held[axis] ?? cfg.default}
                    onChange={(event) => updateHeld(axis, Number(event.target.value))}
                  />
                  <output>{formatNumber(request.held[axis] ?? cfg.default)}</output>
                </label>
              );
            })}
          </div>
        </aside>

        <section className="mannequin-stage">
          <header className="stage-header">
            <div>
              <h1>{viewMode === "all" ? "all outputs" : request.output}({request.x_axis}, {request.y_axis})</h1>
              <p>
                {surface
                  ? `${surface.vehicle} seed ${surface.seed}, method=${surface.method}; held ${surface.held_summary}`
                  : "Evaluating surface..."}
              </p>
            </div>
            <div className="status-chip">{surfaceLoading ? "updating" : "ready"}</div>
          </header>
          {error && <div className="error-strip">{error}</div>}
          <div className="plot-shell">
            {surface ? (
              <MannequinSurfaceGrid
                metadata={metadata}
                outputs={visibleOutputs}
                surface={surface}
                viewMode={viewMode}
              />
            ) : (
              <div className="plot-empty">Evaluating aircraft model...</div>
            )}
          </div>
          <div className="metric-row">
            <div><span>min</span><strong>{formatNumber(aggregateStats?.min)}</strong></div>
            <div><span>mean</span><strong>{formatNumber(aggregateStats?.mean)}</strong></div>
            <div><span>max</span><strong>{formatNumber(aggregateStats?.max)}</strong></div>
            <div><span>overlays</span><strong>{visibleOverlayCount}</strong></div>
          </div>
        </section>

        <aside className="feature-panel">
          <div className="section-title">Feature Inventory</div>
          <div className="feature-list">
            {(surface?.features ?? []).map((feature, index) => (
              <article key={`${feature.type}-${index}`} className="feature-item">
                <h3>{featureTitle(feature)}</h3>
                {feature.description && <p>{feature.description}</p>}
                {feature.manifold && <MathText>{String(feature.manifold)}</MathText>}
                {feature.axes && <div className="axis-tags">{feature.axes.map((axis) => <span key={axis}>{axis}</span>)}</div>}
              </article>
            ))}
          </div>
        </aside>
      </section>
    </main>
  );
}
